// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The input trace (M3-39, FR-033, 10 §3).
//
// > "Every completed game is submitted as a replay — the machine definition version, seed, and
// > the full input trace — not merely a score."
//
// A score is a claim. A trace is the game itself, and the difference is the whole reason an
// untrusted client is acceptable at all: the server re-simulates the trace and compares. So this
// is a **contract**, not a convenience — the shape written here is the shape M4 posts, the shape
// the validator reads, and the shape a ghost replay is played back from. It lives in `core/`
// because both ends of that exchange run the same code.
//
// **A trace records controls, not consequences.** "The left button went down on step 41,200" —
// never "the left flipper rose", and never "the skill-shot lane rotated". A consequence written
// into the trace is a second opinion about what the machine does, and the moment the rules change
// the two disagree; a control is a fact about the player and stays true forever. It is also the
// reason `Game.pressButton` and `Game.pressLaunchButton` had to move into the core: what a button
// *does* depends on what the machine is doing, and a validator has a `Game` and a trace and
// nothing else.
//
// **Steps, not milliseconds.** Input is sampled by the simulation at a fixed 1000 Hz, so an
// action lands on an exact step and a replay applies it on that step. This is what makes a
// tap-passed skill shot repeatable rather than a lottery on frame timing (PH-024).
//
// **Checkpoints locate a divergence rather than merely detecting one.** A score that comes out
// wrong tells you the run diverged; a hash every few seconds tells you *when*, which is the
// difference between "the C# core disagrees" and "the C# core disagrees somewhere in a
// twelve-minute game" (PH-088, and 10 §3's `divergent` outcome).

import { DEFAULT_INITIALS } from '../game/Game'
import type { Game } from '../game/Game'
import type { World } from '../sim/World'

/**
 * The build of the simulation a trace was recorded against.
 *
 * Bumped whenever a change would make an old trace re-simulate differently — which is a
 * deliberately heavy commitment, because 10 §3's `unsupported` outcome means every board scoped
 * to the old version keeps its scores and stops accepting new ones. It is not a release number.
 *
 * **`3` is M9-44**, the accelerating bonus count-up. The change awards exactly the same total, and
 * that is precisely why it needed a bump: the count-up now runs for a *different number of steps*,
 * `finishBall` is called from inside the last tick, so every input recorded after the first bonus
 * of a game lands on a different step of the next ball. Replaying the hundred recorded games
 * against the new core produced different scores — `game-000` at 2,782,000 against a recorded
 * 3,584,500 — which is the whole failure mode this constant exists to make visible rather than
 * silent. The fixtures were re-recorded in the same change.
 *
 * **`4` is the ramp footprint.** A ramp now stands its low sides and its underside on the playfield
 * layer (`sim/devices/footprint.ts`), where a ball on the wood used to pass straight through them —
 * reported as ramps that "are not physically responding when a ball hits the sides or bottom".
 * That is new geometry in the world, so any recorded game in which a ball met a ramp from outside
 * re-simulates differently, the hundred recorded games among them; they were re-recorded in the
 * same change.
 */
export const CORE_VERSION = '4'

/** One thing a player did. The closed vocabulary a validator has to understand. */
export type TraceAction =
  | { readonly kind: 'flipper'; readonly side: 'left' | 'right'; readonly down: boolean }
  | { readonly kind: 'launch'; readonly power: number }
  | { readonly kind: 'nudge'; readonly ix: number; readonly iy: number }
  /**
   * The player declined the high-score table (M2-19a).
   *
   * A control with no argument, and a fourth kind rather than a flag on `launch`: what a button
   * does already depends on what the machine is doing — `pressLaunchButton` is three buttons wired
   * to one — and overloading it a fourth time would make a trace that says "launch" mean "and also
   * possibly refuse the table", which is a consequence and not a control.
   *
   * **Additive, so no `CORE_VERSION` bump.** No trace recorded before this existed contains one,
   * and every one of them re-simulates exactly as it did.
   */
  | { readonly kind: 'skip' }
  /**
   * A coin paid into the coin door (06 §2.4, M7-35).
   *
   * A fifth kind for the same reason `skip` is a fourth: a credit is a control the player used,
   * and the only alternative was to leave `Game.credits` moving with nothing in the trace to
   * explain it. That is not a cosmetic difference — **`credits` is inside `Game.fingerprint`**, so
   * it is folded into every checkpoint hash. A client that added one silently would produce
   * checkpoints a server re-simulating the same trace cannot reproduce, and the score it rejects
   * would be an honest one.
   *
   * **Additive, so no `CORE_VERSION` bump**, on `skip`'s own argument: no trace recorded before
   * this existed contains one, and every one of them re-simulates exactly as it did.
   */
  | { readonly kind: 'credit' }

/** One action, and the simulation step it was sampled on. */
export interface TraceEvent {
  readonly step: number
  readonly action: TraceAction
}

/** A game's state, hashed, at a step. */
export interface TraceCheckpoint {
  readonly step: number
  readonly hash: number
}

/** A whole game, as the bytes needed to play it again. */
export interface ReplayTrace {
  readonly machineId: string
  readonly machineVersion: string
  readonly coreVersion: string
  readonly seed: number
  readonly playerCount: number
  readonly ballsPerGame: number
  /**
   * The three characters initials entry opened on, when it was not `DEFAULT_INITIALS` (M2-19a).
   *
   * **Optional, and absent means 'AAA'** — which is what makes this a compatible addition rather
   * than a version bump: every trace recorded before the field existed carries no value, resolves
   * to the old constant, and reaches the identical game.
   *
   * It is in the header beside `seed` and `playerCount` because it is the same kind of thing: a
   * fact about how the game was set up that the player did not do and a validator cannot infer.
   * The player's last-used initials are remembered by the browser, and the core has no storage —
   * so this is how a remembered value crosses into a simulation both ends have to agree about.
   */
  readonly startingInitials?: string
  readonly events: readonly TraceEvent[]
  readonly checkpoints: readonly TraceCheckpoint[]
  /** How many steps the recording covers. */
  readonly steps: number
  /** What the client says the score was. The thing the server is checking. */
  readonly claimedScore: number
}

/**
 * How often the state is hashed, in steps — every two seconds.
 *
 * A trade with two ends. Rarer, and a divergence report says "somewhere in the last minute",
 * which for a cross-runtime disagreement is barely better than nothing. More often, and a
 * two-hour trace carries 7,200 checkpoints, which is larger than the input trace it accompanies.
 */
export const CHECKPOINT_STEPS = 2000

/** 10 §3's cap: two hours of play. A trace longer than this is refused, not truncated. */
export const MAX_TRACE_STEPS = 7_200_000

/**
 * Hash a game's fingerprint to one 32-bit number.
 *
 * FNV-1a over the fingerprint's integers, with `Math.imul` so the multiply wraps the same way in
 * every runtime — the whole point is that C# reaches this number too, and `a * b` on two large
 * binary64 integers does not wrap, it loses the low bits.
 */
export function hashFingerprint(values: readonly number[]): number {
  let hash = 0x811c9dc5
  for (const value of values) {
    // Two 16-bit halves rather than the whole number: a score is well past 2^32 and its high
    // bits would otherwise be discarded by the xor, which is exactly where a divergence hides.
    const low = value | 0
    const high = Math.floor(value / 0x100000000) | 0
    hash = Math.imul(hash ^ (low & 0xffff), 0x01000193)
    hash = Math.imul(hash ^ ((low >>> 16) & 0xffff), 0x01000193)
    hash = Math.imul(hash ^ (high & 0xffff), 0x01000193)
  }
  return hash >>> 0
}

/**
 * Fold one binary64 into a hash, bit-exactly.
 *
 * A ball's position is a double, and comparing doubles across two runtimes means comparing their
 * *bits* — `Math.round`ing them to something friendlier would hide exactly the divergence this
 * exists to find, because two runtimes that disagree in the last place round to the same number
 * for a long time before they disagree in a way anybody can see.
 */
const scratch = new Float64Array(1)
const asBits = new Uint32Array(scratch.buffer)

function hashDouble(hash: number, value: number): number {
  scratch[0] = value
  let out = Math.imul(hash ^ (asBits[0]! & 0xffff), 0x01000193)
  out = Math.imul(out ^ ((asBits[0]! >>> 16) & 0xffff), 0x01000193)
  out = Math.imul(out ^ (asBits[1]! & 0xffff), 0x01000193)
  return Math.imul(out ^ ((asBits[1]! >>> 16) & 0xffff), 0x01000193)
}

/**
 * The hash of a game right now — **the sequencing and the physics**.
 *
 * The game's own fingerprint is scores, balls played, variables and modes. That is everything a
 * *rule* can be wrong about and nothing a *ball* can be: two runs whose balls are a millimetre
 * apart carry identical fingerprints until the difference finally changes a score, which on a
 * long game can be minutes later and several shots away from where it started.
 *
 * Since the entire purpose of a checkpoint is to say **where** two runtimes stopped agreeing
 * (PH-088, and 10 §3's `divergent` outcome), the ball state has to be in it. Position, velocity
 * and spin, bit for bit, in the world's own fixed ball order.
 */
export function checkpointOf(game: Game): TraceCheckpoint {
  let hash = hashFingerprint(game.fingerprint())
  hash = hashWorld(hash, game.world)
  return { step: game.step, hash: hash >>> 0 }
}

/** Fold every ball's state into a running hash, in the world's own order. */
function hashWorld(hash: number, world: World): number {
  let out = hash
  for (const ball of world.balls) {
    out = Math.imul(out ^ (ball.active ? 1 : 0), 0x01000193)
    if (!ball.active) continue
    for (const value of [ball.x, ball.y, ball.z, ball.vx, ball.vy, ball.vz, ball.wx, ball.wy, ball.wz]) {
      out = hashDouble(out, value)
    }
  }
  return out
}

/**
 * Is this a trace this build can run?
 *
 * Answers with a reason rather than a boolean: every one of these is a distinct outcome in
 * 10 §3's table, and "could not replay it" is not a thing a player can be told.
 */
export function traceProblem(trace: ReplayTrace): string | undefined {
  if (trace.coreVersion !== CORE_VERSION) {
    return `recorded on core ${trace.coreVersion}; this build runs core ${CORE_VERSION}`
  }
  if (trace.steps > MAX_TRACE_STEPS) {
    return `${trace.steps} steps is past the ${MAX_TRACE_STEPS} cap`
  }
  if (trace.steps < 0 || !Number.isInteger(trace.steps)) return `${trace.steps} is not a step count`
  let previous = -1
  for (const event of trace.events) {
    // Ascending, because the runner walks the trace once alongside the simulation. Out of order,
    // an event would be silently skipped and the replay would diverge for no visible reason.
    if (!Number.isInteger(event.step) || event.step < previous) {
      return `events must ascend by step; ${event.step} follows ${previous}`
    }
    previous = event.step
  }
  return undefined
}

/**
 * The key that makes two submissions of one game one score.
 *
 * **Derived from the game, never generated for it.** 09 §6 asks for a "client-generated UUID"
 * against each queued replay, and what it means by that is *unique per game*, not *drawn from a
 * random number generator*. The two come apart the moment a client is killed: the native client is
 * killed whenever the headset comes off a head, which is the ordinary way a session ends there, so
 * a key held in memory does not survive to the retry and the next launch posts the same game a
 * second time. A key computed from the trace is the same key on every attempt, on every launch, on
 * a restored backup and on a different device.
 *
 * **It lives in the core because both clients have to agree on it.** The server's uniqueness
 * constraint is `(profile_id, idempotency_key)`, so a browser and a headset that disagreed about
 * one game's key would double-post it — and the whole point of holding two implementations
 * identical by fixture is that questions like this get one answer. `core/fixtures/parity/
 * submission-key-v1.json` is that fixture.
 *
 * Four independent values, because a key is a promise not to swallow somebody's score by accident:
 * the seed, the length, the simulation's own last checkpoint hash, and a hash over every field the
 * trace carries. Written in hex so the result satisfies the server's `^[A-Za-z0-9_-]{8,128}$` by
 * construction rather than by checking.
 */
export function submissionKey(trace: ReplayTrace): string {
  let hash = 0x811c9dc5
  hash = hashText(hash, trace.machineId)
  hash = hashText(hash, trace.machineVersion)
  hash = hashText(hash, trace.coreVersion)
  hash = hashInt(hash, trace.seed)
  hash = hashInt(hash, trace.playerCount)
  hash = hashInt(hash, trace.ballsPerGame)
  // **Only when it is not the default**, so that a trace without the field and a trace that names
  // 'AAA' — which are the same game — produce the same key, and so that every key already posted
  // against a score stays exactly what it was. A key is a wire value; adding a field to the header
  // must not re-key the games that never used it.
  if (trace.startingInitials !== undefined && trace.startingInitials !== DEFAULT_INITIALS) {
    hash = hashText(hash, trace.startingInitials)
  }
  hash = hashInt(hash, trace.steps)
  hash = hashDouble(hash, trace.claimedScore)

  // Lengths are folded in as well as contents. Without them a trace whose last event was dropped
  // would hash the same as one that never had it, and "the game I played" and "the game minus its
  // final flip" are not the same submission.
  hash = hashInt(hash, trace.events.length)
  for (const event of trace.events) {
    hash = hashInt(hash, event.step)
    const action = event.action
    if (action.kind === 'flipper') {
      hash = hashInt(hash, ACTION_FLIPPER)
      hash = hashInt(hash, action.side === 'left' ? 1 : 0)
      hash = hashInt(hash, action.down ? 1 : 0)
    } else if (action.kind === 'launch') {
      hash = hashInt(hash, ACTION_LAUNCH)
      hash = hashDouble(hash, action.power)
    } else if (action.kind === 'credit') {
      // Its own code with nothing after it, for the reason the `skip` branch below states.
      hash = hashInt(hash, ACTION_CREDIT)
    } else if (action.kind === 'skip') {
      // **Its own code, and nothing after it.** Falling through to the nudge branch — which is
      // what an `else` would have done — would have hashed `undefined` twice through
      // `hashDouble`, so every trace containing a skip would key on NaN and two different games
      // would collide.
      hash = hashInt(hash, ACTION_SKIP)
    } else {
      hash = hashInt(hash, ACTION_NUDGE)
      hash = hashDouble(hash, action.ix)
      hash = hashDouble(hash, action.iy)
    }
  }

  hash = hashInt(hash, trace.checkpoints.length)
  for (const checkpoint of trace.checkpoints) {
    hash = hashInt(hash, checkpoint.step)
    hash = hashInt(hash, checkpoint.hash)
  }

  const tail =
    trace.checkpoints.length > 0 ? trace.checkpoints[trace.checkpoints.length - 1]!.hash : 0
  return `g-${hex8(trace.seed)}-${hex8(trace.steps)}-${hex8(tail)}-${hex8(hash)}`
}

/**
 * The action codes the key is built from.
 *
 * Written out rather than taken from the order of a union, because a key is a wire value: adding a
 * fourth action must not renumber the three that already have scores posted against them.
 */
const ACTION_FLIPPER = 0
const ACTION_LAUNCH = 1
const ACTION_NUDGE = 2
const ACTION_SKIP = 3
const ACTION_CREDIT = 4

/** Fold one integer in, in the two 16-bit halves `hashFingerprint` uses. */
function hashInt(hash: number, value: number): number {
  const low = value | 0
  let out = Math.imul(hash ^ (low & 0xffff), 0x01000193)
  out = Math.imul(out ^ ((low >>> 16) & 0xffff), 0x01000193)
  return out
}

/**
 * Fold one string in, length first.
 *
 * By UTF-16 code unit, which is what both runtimes' strings are made of — encoding to UTF-8 first
 * would be a second thing for the two sides to agree about for no benefit, since a machine slug
 * that is not ASCII would have to round-trip identically either way.
 */
function hashText(hash: number, value: string): number {
  let out = hashInt(hash, value.length)
  for (let i = 0; i < value.length; i++) out = hashInt(out, value.charCodeAt(i))
  return out
}

/** An unsigned 32-bit number as eight hex digits. */
function hex8(value: number): string {
  return (value >>> 0).toString(16).padStart(8, '0')
}
